<template>
  <div class="wrapper">
    <dashboardNav/>

    <div id="content">
      <div class="container">  <!-- Alerts --> </div>

      <div class="main container">
        <loader :isLoading = "requestInProgress"/>
        <router-link class="simple-link" v-bind:to="{ name: 'PassDetailView', params: { id: passDetail.id } }">
          <i class="fa fa-arrow-left" aria-hidden="true"></i>&nbsp; Back to Pass Detail
        </router-link>
        <br/><br/>
        <h2>{{ passDetail.pass_name }}</h2>
        <p>Type of Pass: {{ passDetail.type_of_pass }}</p>

        <div class="line"></div>

        <div class="invite container">
          <!-- Invite -->
          <h3 class="">Invite</h3>

          <div class="form-group">
            <vue-tags-input
              v-model="tag"
              placeholder="john@mail.com"
              :tags="tags"
              :validation="validation"
              @tags-changed="newTags => tags = newTags"
            />
          </div>
        </div>

        <div class="text-center">
          <button type="button" class="btn btn-primary" @click="doSendInvite()">
            Send Invite
          </button>
          <button type="button" class="btn btn-cancel" @click="doCancel()">
            Cancel
          </button>
        </div>
      </div>

      <br/>
      <small v-if="errorMessage" class="form-text text-muted error-message text-center">
        {{ errorMessage }}
      </small>

      <section class="container">
        <h3>Invited Pass</h3>
        <invite-list-view
          :invites="invites"
          :hasInvites="hasInvites"
          :deleteInvite="deleteInvite"
          :resendInvite="resendInvite" />
      </section>
    </div>
  </div>
</template>

<script>
import VueTagsInput from '@johmun/vue-tags-input'
import { required, email } from 'vuelidate/lib/validators'

import Loader from '@/components/common/Loader'
import DashboardNav from '@/components/common/DashboardNav'
import InputComponent from '@/components/form/InputComponent'
import InvitesListView from '@/components/listview/InvitesListView'
import PassInviteEmailForm from '@/components/passes/PassInviteEmailForm'

import Sidebar from '@/containers/dashboard/Sidebar'

import { AuthService } from '@/api/AuthService'
import { APIService } from '@/api/APIService'

const auth = new AuthService()
const api = new APIService()

export default {
  components: {
    VueTagsInput,
    Loader,
    'invite-list-view': InvitesListView,
    'sidebar': Sidebar,
    dashboardNav: DashboardNav,
    InputComponent,
    PassInviteEmailForm
  },
  validations: {
    form: {
      email: { required, email },
    }
  },
  data() {
    return {
      form: {
        email: ''
      },
      typeOfPass: '',
      passId: this.$route.params.id,
      passDetail: {},
      invites: [],
      hasInvites: false,
      requestInProgress: false,
      haveNewEmails: false,
      errorMessage: '',
      tag: '',
      tags: [],
      validation: [
        {
          classes: 'invalid-email',
          rule: /\S+@\S+\.\S+/,
          disableAdd: true,
        },
        {
          classes: 'no-braces',
          rule: ({ text }) => text.indexOf('{') !== -1 || text.indexOf('}') !== -1,
        }
      ],
    }
  },
  mounted(){
    this.getPassDetail()
  },
  methods: {
    // async functions
    async getPassDetail() {
      try {
        this.requestInProgress = true
        const passDetail = await api.getPassInviteScans(this.passId)
        this.passDetail = passDetail
        this.invites = passDetail.invites_data
        this.invites.sort((a, b) => { return a.status.localeCompare(b.status) })
        this.typeOfPass = passDetail.type_of_pass
        this.hasInvites = this.invites.length !== 0
        this.requestInProgress = false
      } catch(e) {
        if(this.passDetail.message && this.passDetail.message === 'Unauthorized') {
          this.logout()
        }
      }
    },
    async addAndSendInviteEmail(emails) {
      // update invites list API call
      const updateInviteParams = {
        pass_id: this.passDetail.id,
        user_id: this.passDetail.owner_id,
        invites: emails
      }
      await api.createUpdatePassInvites(updateInviteParams)
      // end update invites list API call

      this.showInviteSentToast()
    },
    async resendInvite(email) {
      this.showResendSentToast()
      await this.sendEmailInvite(email)
      this.showInviteSentToast()
    },
    async sendEmailInvite(userEmail){
      const passDate = `${this.passDetail.visiting_date} - ${this.passDetail.visiting_time}`
      const emailParams = {
        link_id: this.passDetail.shareable_link_id,
        company_name: this.passDetail.company_name,
        email: userEmail,
        pass_date: passDate,
        pass_reason: this.passDetail.reason_for_visit
      }

      await api.sendInviteEmail(emailParams)
    },
    async deleteInviteCall(userEmail) {
      // delete from invites list API call
      const params = { invites: {email: userEmail} }
      await api.deleteUserFromInviteList(this.passId, params)
      // end delete from invites list API call
    },
    // non-async functions
    doSendInvite() {
      this.errorMessage = ''

      if(this.tags.length === 0) {
        this.errorMessage = 'Please add an email'
      } else {
        if(this.typeOfPass === 'Solo Pass' && this.invites.length === 1) {
          this.errorMessage = 'Cannot add another email since this is a Solo pass'
        } else if(this.typeOfPass === 'Solo Pass' && this.tags.length !== 1) {
            this.errorMessage = 'Cannot add another email since this is a Solo pass'
        } else {
          this.sendInvite()
        }
      }
    },
    sendInvite() {
      const invites = this.invites.map(i => i.email)
      const emails = this.tags.map(i => {
        return { email: i.text.toLowerCase(), status: 'Pending' }
      })

      const filteredEmails = emails.filter(function(item){
        return invites.indexOf(item.email) === -1;
      })
      this.invites.push(...filteredEmails)
      this.hasInvites = this.invites.length !== 0
      this.haveNewEmails = filteredEmails.length !== 0
      // reset field
      this.tags = []
      this.tag = ''
      if(filteredEmails.length === 0) {
        this.$toasted.show('Email is already in the list.' +
          'Please click, resend email if user haven’t accepted it yet.', {
        	theme: 'toasted-primary',
        	position: 'top-right',
        	duration : 2000
        })
      } else {
        this.addAndSendInviteEmail(filteredEmails)
      }
    },
    deleteInvite(data) {
      this.invites = this.invites.filter(i => {
        return i.email !== data.email
      })
      this.hasInvites = this.invites.length !== 0
      const userEmail = data.email.toLowerCase()
      this.deleteInviteCall(userEmail)
    },
    showResendSentToast(){
      this.$toasted.show('Resending Invite...', {
      	theme: 'toasted-primary',
      	position: 'top-right',
      	duration : 1000
      })
    },
    showInviteSentToast(){
      this.$toasted.show('Invite sent', {
      	theme: 'toasted-primary',
      	position: 'top-right',
      	duration : 2000
      })
    },
    doCancel() {
      this.$router.push({ name: 'PassDetailView' })
    },
    logout() {
      auth.logout()
      this.$router.push('/login')
    }
  }
}
</script>

<style type="text/css" scoped="">
  /deep/ h3 {
    font-size: 25px;
  }

  /deep/.btn-primary {
    min-width: 140px;
  }

  .btn-cancel {
    margin-top: 30px;
    margin-bottom: 10px;
    margin-left: 20px;
    border: none;
    background-color: #E8E8E8;
    color: #00386a;
    font-family: 'HKGrotesk-Bold', Helvetica, sans-serif;
    font-size: 16px;
    min-width: 140px;
    border-radius: 3px;
    padding: 8px 0;
  }
</style>



// WEBPACK FOOTER //
// src/containers/passes/PassInvite.vue